// Work › Automations (`/c/{slug}/work/automations`; Parity P5, hybrid design §6.1): every rule
// the caller can see, in the order they run (⋮⋮ drags, or ↑ ↓ on the handle), each as one
// sentence with its board, its owner (it runs with their access) and how often it fired. The
// row's controls are visible on hover or focus: ⏻ switch, History, ✎ edit, ⧉ duplicate, 🗑
// delete. Agents never write a rule: their proposals sit on top ("Sourcer proposes …") with
// Review → to the approval. A paused rule says why (its owner lost access, or its hourly budget
// ran out); switching it on again re-checks the owner. No rules: an honest empty state with the
// real create actions.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EmptyState,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  BotIcon,
  CopyIcon,
  GripVerticalIcon,
  HistoryIcon,
  PencilIcon,
  PlusIcon,
  PowerIcon,
  SparklesIcon,
  Trash2Icon,
  ZapIcon,
} from "lucide-react";
import { type DragEvent, type KeyboardEvent, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { LoadError, Loading } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { RowAction, RowActions } from "../settings/row-actions";
import { boardsQuery } from "../work/work-queries";
import { type Proposal, processKeys, type Rule, rulesQuery, runsQuery } from "./automation-queries";
import { type BuilderStart, RuleBuilder } from "./rule-builder";

const PAUSE_TEXT: Record<string, string> = {
  owner_lost_access: "Paused: its owner lost access. Switch it on to run it as you.",
  budget_exceeded: "Paused: it used its hourly budget. Switch it on to resume.",
  // T6-BOARD-NOTE: "Pause rules" on the board's How it works
  board_paused: "Paused with the board's rules. Resume them on the board's How it works.",
};

/**
 * New positions for `order` (the wanted order) that keep every rule the caller cannot edit where
 * it is: each run of editable rules between two fixed ones takes evenly spread integers strictly
 * inside their gap (before the first fixed one: below it; after the last: above it). Only the
 * rules whose position changes are returned; null when a gap is too small.
 */
export function spreadAround(order: readonly Rule[]): { r: Rule; position: number }[] | null {
  const out: { r: Rule; position: number }[] = [];
  let run: Rule[] = [];
  let lo: number | null = null;
  const flush = (hi: number | null) => {
    if (!run.length) return true;
    const step = 1000;
    const from = lo ?? (hi === null ? 0 : hi - (run.length + 1) * step);
    const gap = hi === null ? (run.length + 1) * step : hi - from;
    const inc = Math.floor(gap / (run.length + 1));
    if (inc < 1) return false;
    run.forEach((r, i) => {
      const position = Math.max(0, from + inc * (i + 1));
      if (position !== r.position) out.push({ r, position });
    });
    run = [];
    return true;
  };
  for (const r of order) {
    if (r.can_edit) run.push(r);
    else {
      if (!flush(r.position)) return null;
      lo = r.position;
    }
  }
  if (!flush(null)) return null;
  // the result must read in the wanted order (a run squeezed against 0 would not): else refuse
  let last = -1;
  for (const r of order) {
    const p = out.find((x) => x.r.id === r.id)?.position ?? r.position;
    if (p <= last) return null;
    last = p;
  }
  return out;
}

export function AutomationsScreen({
  api,
  company,
  boardId,
  onBoard,
}: {
  api: Api;
  company: CompanyAccess;
  /** Only this board's rules (and the company-wide ones). */
  boardId: string | undefined;
  onBoard: (id: string | undefined) => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const q = useQuery(rulesQuery(api, co));
  const boards = useQuery(boardsQuery(api, co));
  const [builder, setBuilder] = useState<BuilderStart | null>(null);
  const [history, setHistory] = useState<Rule | null>(null);
  const [deleting, setDeleting] = useState<Rule | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: processKeys.rules(co) });
  const fail = (what: string) => (e: unknown) =>
    toast.add({ title: what, description: problemText(e, "Try again"), type: "error" });
  const said = (proposed: boolean, done: string) =>
    toast.add({ title: proposed ? "Sent for approval" : done, type: "success" });

  const all = q.data?.data ?? [];
  const rules = boardId ? all.filter((r) => r.board_id === boardId || r.board_id === null) : all;
  const proposals = (q.data?.proposals ?? []).filter(
    (p) => !boardId || p.board_id === boardId || p.board_id === null,
  );

  const toggle = useMutation({
    mutationFn: (r: Rule) =>
      r.enabled && !r.paused_reason
        ? api.client.automations.disable({ params: { co, id: r.id } })
        : api.client.automations.enable({ params: { co, id: r.id } }),
    onSuccess: (out, r) => {
      refresh();
      said(out.proposed, r.enabled && !r.paused_reason ? "Automation off" : "Automation on");
    },
    onError: fail("The automation was not switched"),
  });
  const duplicate = useMutation({
    mutationFn: (r: Rule) => api.client.automations.duplicate({ params: { co, id: r.id } }),
    onSuccess: (out) => {
      refresh();
      said(out.proposed, "Copied, switched off");
    },
    onError: fail("The automation was not copied"),
  });
  const remove = useMutation({
    mutationFn: (r: Rule) => api.client.automations.delete({ params: { co, id: r.id } }),
    onSuccess: (out) => {
      refresh();
      said(out.proposed, "Automation deleted");
    },
    onError: fail("The automation was not deleted"),
  });
  // One PATCH, the moved rule's: its position becomes the neighbour's it now sits on (the list
  // orders by position, then id); rules the caller does not edit, and their owners, stay as they are.
  const reorder = useMutation({
    mutationFn: async ({ r, position }: { r: Rule; position: number }) => {
      if (r.position === position) return;
      await api.client.automations.update({
        params: { co, id: r.id },
        ifMatch: r.version,
        body: { position },
      });
    },
    onSettled: refresh,
    onError: fail("The order was not saved"),
  });
  // Rules the caller does not edit keep their positions (anchors); each run of editable rules
  // between two anchors is spread evenly in the gap between them. No room: refused, nothing moves.
  const rebalance = useMutation({
    mutationFn: async (order: Rule[]) => {
      const plan = spreadAround(order);
      if (!plan)
        throw new Error("No room to move it here: an automation you cannot edit is in the way");
      for (const { r, position } of plan)
        await api.client.automations.update({
          params: { co, id: r.id },
          ifMatch: r.version,
          body: { position },
        });
    },
    onSettled: refresh,
    onError: fail("The order was not saved"),
  });
  const move = (id: string, to: number) => {
    const order = [...all];
    const from = order.findIndex((r) => r.id === id);
    if (from < 0 || to < 0 || to >= order.length || from === to) return;
    const [r] = order.splice(from, 1);
    if (!r) return;
    // positions are spread out (a new rule's is its creation time in ms): the moved rule takes
    // the midpoint of its new neighbours, or a step past the first or last
    const before = order[to - 1]?.position;
    const after = order[to]?.position;
    const position =
      before === undefined
        ? Math.max(0, (after ?? 0) - 1000)
        : after === undefined
          ? before + 1000
          : Math.floor((before + after) / 2);
    const fits =
      (before === undefined || position > before) && (after === undefined || position < after);
    if (fits) {
      reorder.mutate({ r, position });
      return;
    }
    // no gap left between the neighbours: spread the rules the caller edits out again, in the
    // new order (a move keeps each rule's owner)
    order.splice(to, 0, r);
    rebalance.mutate(order);
  };

  return (
    <div data-screen="automations" className="flex min-h-full min-w-0 flex-col">
      <LocationHeader
        icon={ZapIcon}
        title="Automations"
        {...(q.data ? { count: rules.length } : {})}
        actions={
          <>
            <NativeSelect
              aria-label="Board"
              size="sm"
              value={boardId ?? ""}
              onChange={(e) => onBoard(e.target.value || undefined)}
            >
              <NativeSelectOption value="">All boards</NativeSelectOption>
              {(boards.data?.data ?? []).map((b) => (
                <NativeSelectOption key={b.id} value={b.id}>
                  {b.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            <Button size="sm" onClick={() => setBuilder({ kind: "new", boardId: boardId ?? null })}>
              <PlusIcon aria-hidden="true" />
              New automation
            </Button>
          </>
        }
      />
      <div className="flex min-w-0 flex-1 flex-col gap-4 px-6 py-4 max-md:px-4">
        {q.isPending ? <Loading label="Loading automations" rows={3} /> : null}
        {q.isError ? <LoadError what="Automations" error={q.error} /> : null}

        {proposals.length ? (
          <section aria-label="Proposed by agents" className="flex flex-col gap-1">
            {proposals.map((p) => (
              <ProposalRow key={p.approval_id} company={company} proposal={p} />
            ))}
          </section>
        ) : null}

        {q.data && !rules.length ? (
          <EmptyState
            icon={ZapIcon}
            title="No automations yet"
            description="When something happens on a board, an automation acts for you: set a status, assign, comment, create a task."
            actions={
              <>
                <Button onClick={() => setBuilder({ kind: "new", boardId: boardId ?? null })}>
                  <PlusIcon aria-hidden="true" />
                  New automation
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setBuilder({ kind: "new", boardId: boardId ?? null })}
                >
                  <SparklesIcon aria-hidden="true" />
                  Describe it
                </Button>
              </>
            }
          />
        ) : null}

        {rules.length ? (
          <ul aria-label="Automations" className="flex flex-col divide-y divide-subtle">
            {rules.map((r) => {
              const index = all.findIndex((x) => x.id === r.id);
              const on = r.enabled && !r.paused_reason;
              return (
                <li
                  key={r.id}
                  data-rule={r.id}
                  className="group/row flex items-start gap-3 py-3"
                  onDragOver={(e: DragEvent) => {
                    if (dragging) e.preventDefault();
                  }}
                  onDrop={() => {
                    if (dragging) move(dragging, index);
                    setDragging(null);
                  }}
                >
                  {r.can_edit ? (
                    <button
                      type="button"
                      draggable
                      aria-label={`Move ${r.name} (arrow keys)`}
                      className="mt-0.5 cursor-grab text-muted-foreground opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
                      onDragStart={() => setDragging(r.id)}
                      onDragEnd={() => setDragging(null)}
                      onKeyDown={(e: KeyboardEvent) => {
                        if (e.key === "ArrowUp") move(r.id, index - 1);
                        if (e.key === "ArrowDown") move(r.id, index + 1);
                      }}
                    >
                      <GripVerticalIcon aria-hidden="true" className="size-4" />
                    </button>
                  ) : (
                    <span className="w-4" />
                  )}
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={on ? `Switch off ${r.name}` : `Switch on ${r.name}`}
                    aria-pressed={on}
                    disabled={!r.can_edit || toggle.isPending}
                    className={on ? "text-success" : "text-muted-foreground"}
                    onClick={() => toggle.mutate(r)}
                  >
                    <PowerIcon aria-hidden="true" />
                  </Button>
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-medium">{r.name}</span>
                      {!r.enabled ? <Badge variant="outline">Off</Badge> : null}
                      {r.paused_reason ? <Badge variant="warning">Paused</Badge> : null}
                      {r.source === "agent" ? (
                        <Badge variant="outline">
                          <BotIcon aria-hidden="true" />
                          {r.proposed_by_name ?? "Agent"}'s idea
                        </Badge>
                      ) : null}
                    </span>
                    <span className="text-sm text-muted-foreground">{r.sentence}</span>
                    {r.paused_reason ? (
                      <span className="text-xs text-warning">{PAUSE_TEXT[r.paused_reason]}</span>
                    ) : null}
                    <span className="text-xs text-muted-foreground">
                      {r.board_name ?? "Every board"} · runs as{" "}
                      {r.owner_de_name ? `${r.owner_de_name} (DE)` : (r.owner_name ?? "its owner")}{" "}
                      ·{" "}
                      {r.fired_count
                        ? `fired ${r.fired_count}×${r.last_fired_at ? `, last ${formatDateTime(r.last_fired_at)}` : ""}`
                        : "not fired yet"}
                    </span>
                  </div>
                  <RowActions>
                    <RowAction
                      icon={HistoryIcon}
                      label={`History of ${r.name}`}
                      tip="History"
                      onClick={() => setHistory(r)}
                    />
                    {r.can_edit ? (
                      <>
                        <RowAction
                          icon={PencilIcon}
                          label={`Edit ${r.name}`}
                          tip="Edit"
                          onClick={() => setBuilder({ kind: "edit", rule: r })}
                        />
                        <RowAction
                          icon={CopyIcon}
                          label={`Duplicate ${r.name}`}
                          tip="Duplicate"
                          onClick={() => duplicate.mutate(r)}
                        />
                        <RowAction
                          icon={Trash2Icon}
                          label={`Delete ${r.name}`}
                          tip="Delete"
                          onClick={() => setDeleting(r)}
                        />
                      </>
                    ) : null}
                  </RowActions>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>

      {builder ? (
        <RuleBuilder api={api} company={company} start={builder} onClose={() => setBuilder(null)} />
      ) : null}
      {history ? (
        <HistoryDialog
          api={api}
          company={company}
          rule={history}
          onClose={() => setHistory(null)}
        />
      ) : null}
      <AlertDialog open={deleting !== null} onOpenChange={(o) => (o ? null : setDeleting(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleting?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              It stops at once and its history goes with it. Switch it off instead to keep both.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (deleting) remove.mutate(deleting);
                setDeleting(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ProposalRow({ company, proposal: p }: { company: CompanyAccess; proposal: Proposal }) {
  return (
    <div
      data-proposal={p.approval_id}
      className="flex items-center gap-3 rounded-md border border-dashed border-subtle px-3 py-2"
    >
      <BotIcon aria-hidden="true" className="size-4 text-muted-foreground" />
      <span className="min-w-0 flex-1 text-sm">
        <span className="font-medium">{p.requested_by_name ?? "An agent"}</span> proposes:{" "}
        <span className="text-muted-foreground">{p.sentence}</span>
      </span>
      <Link
        to="/c/$slug/agents/approvals/$id"
        params={{ slug: company.slug, id: p.approval_id }}
        className="text-sm font-medium text-primary hover:underline"
      >
        Review →
      </Link>
    </div>
  );
}

const OUTCOME: Record<
  string,
  { label: string; variant: "success" | "warning" | "outline" | "danger" }
> = {
  fired: { label: "Fired", variant: "success" },
  partial: { label: "Partly", variant: "warning" },
  refused: { label: "Refused", variant: "danger" },
  skipped: { label: "Skipped", variant: "outline" },
};

function HistoryDialog({
  api,
  company,
  rule,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  rule: Rule;
  onClose: () => void;
}) {
  const q = useQuery(runsQuery(api, company.id, rule.id));
  const runs = q.data?.data ?? [];
  return (
    <Dialog open onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="sm:max-w-lg" data-slot="rule-history">
        <DialogHeader>
          <DialogTitle>History: {rule.name}</DialogTitle>
          <DialogDescription>
            Each firing, newest first, with what each action did.
          </DialogDescription>
        </DialogHeader>
        {q.isPending ? <Loading label="Loading history" rows={2} /> : null}
        {q.isError ? <LoadError what="History" error={q.error} /> : null}
        {q.data && !runs.length ? (
          <p className="text-sm text-muted-foreground">It has not fired yet.</p>
        ) : null}
        <ul className="flex max-h-96 flex-col gap-2 overflow-y-auto">
          {runs.map((r) => (
            <li key={r.id} className="flex flex-col gap-1 border-b border-subtle pb-2 text-sm">
              <span className="flex items-center gap-2">
                <Badge variant={OUTCOME[r.outcome]?.variant ?? "outline"}>
                  {OUTCOME[r.outcome]?.label ?? r.outcome}
                </Badge>
                <span className="text-muted-foreground">{formatDateTime(r.created_at)}</span>
                {r.acted_as_name ? (
                  <span className="text-xs text-muted-foreground">
                    by {r.acted_as_name}
                    {r.acted_as_de ? " (DE)" : ""}
                  </span>
                ) : null}
                {r.depth ? (
                  <span className="text-xs text-muted-foreground">chain depth {r.depth}</span>
                ) : null}
              </span>
              {r.refused_reason ? (
                <span className="text-muted-foreground">{r.refused_reason}</span>
              ) : null}
              {r.effects.map((e, i) => (
                <span
                  // biome-ignore lint/suspicious/noArrayIndexKey: a firing's effects are its actions, in order, never reordered
                  key={`${r.id}-${i}`}
                  className={e.ok ? "text-muted-foreground" : "text-danger"}
                >
                  {e.ok ? "✓" : "✗"} {e.action.replaceAll("_", " ")}
                  {e.detail ? `: ${e.detail}` : ""}
                </span>
              ))}
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
